import { encode, decode } from "../../shared/src/cbor";
import {
  type ClientMessage,
  type ServerMessage,
  type QueryType,
  type MerkleBucket,
  type MerkleGroup,
  PROTOCOL_VERSION,
} from "../../shared/src/protocol";

export const APP_VERSION = 1;

import type { CommandEnvelope } from "../../shared/src/commands";
import type { EventEnvelope } from "../../shared/src/events";
import { db, getSqlocal, reportDbError } from "./db";
import { events, pendingCommands } from "../../shared/src/schema";
import { eq, inArray, sql } from "drizzle-orm";
import {
  uploadAllPending,
  uploadBlob,
  getPendingBlobCount,
  onBlobStored,
} from "./blobs";
import {
  pruneCases,
  pinCase,
  unpinCase,
  markCaseAccessed,
  getCasesToKeep,
} from "./retention";
import { uuidv7 } from "../../shared/src/ids";

// ── Watch-event DOM bridge for viewer sessions ────────────────────────
// When the server pushes a live watchEvent.* to a viewer (who has no BLE),
// we dispatch a biostream:watch-event DOM event so live-triage.tsx updates.

function dispatchWatchEventIfNeeded(event: EventEnvelope): void {
  const p = event.payload as Record<string, any>;

  if (event.type === "watchEvent.triageAlert") {
    const vitals = p.payload ?? {};
    window.dispatchEvent(new CustomEvent("biostream:watch-event", {
      detail: {
        type: "TRIAGE_ALERT",
        data: {
          watchId:     p.watchId,
          soldierId:   p.soldierUuid ?? null,
          soldierName: null, // viewer looks up via pairings built from history
          triageLevel: vitals.triageLevel ?? null,
          triageScore: vitals.triageScore ?? null,
          confidence:  vitals.confidence  ?? null,
          hrBpm:       vitals.hrBpm       ?? null,
          spo2Pct:     vitals.spo2Pct     ?? null,
          batteryPct:  vitals.batteryPct  ?? null,
          lat: p.gpsLat ?? null,
          lon: p.gpsLon ?? null,
        },
      },
    }));
  }

  if (event.type === "watchEvent.manDown") {
    window.dispatchEvent(new CustomEvent("biostream:watch-event", {
      detail: {
        type: "MAN_DOWN",
        data: {
          watchId:    p.watchId,
          soldierId:  p.soldierUuid ?? null,
          soldierName: null,
          lat: p.gpsLat ?? null,
          lon: p.gpsLon ?? null,
        },
      },
    }));
  }

  if (event.type === "watchEvent.statusResponse") {
    window.dispatchEvent(new CustomEvent("biostream:watch-event", {
      detail: {
        type: "STATUS_RESPONSE",
        data: {
          watchId:    p.watchId,
          soldierId:  p.soldierUuid ?? null,
          hrBpm:      p.hrBpm      ?? null,
          spo2Pct:    p.spo2Pct    ?? null,
          batteryPct: p.batteryPct ?? null,
          lat: p.gpsLat ?? null,
          lon: p.gpsLon ?? null,
        },
      },
    }));
  }
}

// ── Entity hash helpers (denormalized in entity_hashes table) ────────

// FNV-1a 32-bit — synchronous, no crypto.subtle overhead
function fnv1a(data: string): string {
  let h = 0x811c9dc5;
  for (let i = 0; i < data.length; i++) {
    h ^= data.charCodeAt(i);
    h = Math.imul(h, 0x01000193);
  }
  return (h >>> 0).toString(16).padStart(8, "0");
}

/** Recompute and store the hash for a single entity. */
async function updateEntityHash(entityId: string): Promise<void> {
  const sq = getSqlocal();
  const rows =
    (await sq.sql`SELECT id FROM events WHERE entity_id = ${entityId} ORDER BY id`) as {
      id: string;
    }[];
  const h = fnv1a(rows.map((r) => r.id).join(","));
  await sq.sql`INSERT OR REPLACE INTO entity_hashes (entity_id, hash) VALUES (${entityId}, ${h})`;
}

/** Recompute and store hashes for multiple entities. Single SELECT, batch INSERT. */
async function updateEntityHashes(entityIds: string[]): Promise<void> {
  if (entityIds.length === 0) return;
  const unique = [...new Set(entityIds)];
  const sq = getSqlocal();

  // Get all event IDs grouped by entity — sort in JS to avoid GROUP_CONCAT ordering issues
  const allRows = (await sq.sql`
    SELECT entity_id, id FROM events ORDER BY entity_id, id
  `) as { entity_id: string; id: string }[];

  const entityEvents = new Map<string, string[]>();
  for (const row of allRows) {
    const list = entityEvents.get(row.entity_id) || [];
    list.push(row.id);
    entityEvents.set(row.entity_id, list);
  }

  // Batch all INSERTs in a single worker round-trip
  await sq.batch((sql) =>
    unique.map((eid) => {
      const ids = entityEvents.get(eid);
      const h = ids ? fnv1a(ids.join(",")) : fnv1a("");
      return sql`INSERT OR REPLACE INTO entity_hashes (entity_id, hash) VALUES (${eid}, ${h})`;
    }),
  );
}

/** Derive a 4-char hex bucket prefix by hashing the entityId (uniform distribution). */
function getBucketPrefix(entityId: string): string {
  return fnv1a(entityId).slice(0, 4);
}

interface ClientMerkleTree {
  root: string;
  groups: MerkleGroup[];
  /** Leaf buckets kept locally for drilldown comparison */
  buckets: MerkleBucket[];
}

/**
 * Resolve the local entity set that should be included in the Merkle tree.
 * Mirrors the server's resolveScope: ref data + subscribed cases + their sub-entities.
 */
async function resolveLocalScope(caseIds: string[]): Promise<Set<string>> {
  const sq = getSqlocal();
  const scope = new Set<string>();

  // 1. Reference entities (regions, hospitals, units)
  const refs = (await sq.sql`
    SELECT id FROM regions
    UNION ALL SELECT id FROM hospitals
    UNION ALL SELECT id FROM units
  `) as { id: string }[];
  for (const r of refs) scope.add(r.id);

  // 2. For each case: case + patient + all sub-entity IDs
  for (const caseId of caseIds) {
    scope.add(caseId);
    const caseRows =
      (await sq.sql`SELECT patient_id FROM cases WHERE id = ${caseId}`) as {
        patient_id: string;
      }[];
    if (caseRows[0]?.patient_id) scope.add(caseRows[0].patient_id);

    const subs = (await sq.sql`
      SELECT id FROM case_vitals WHERE case_id = ${caseId}
      UNION ALL SELECT id FROM case_ventilation WHERE case_id = ${caseId}
      UNION ALL SELECT id FROM case_diagnostics WHERE case_id = ${caseId}
      UNION ALL SELECT id FROM case_lab_values WHERE case_id = ${caseId}
      UNION ALL SELECT id FROM case_exam WHERE case_id = ${caseId}
      UNION ALL SELECT id FROM case_cpr WHERE case_id = ${caseId}
      UNION ALL SELECT id FROM case_tourniquet WHERE case_id = ${caseId}
      UNION ALL SELECT id FROM case_medication WHERE case_id = ${caseId}
      UNION ALL SELECT id FROM case_surgery WHERE case_id = ${caseId}
      UNION ALL SELECT id FROM attachments WHERE case_id = ${caseId}
    `) as { id: string }[];
    for (const r of subs) scope.add(r.id);
  }

  return scope;
}

async function computeClientMerkleTree(
  allowedEntityIds?: Set<string>,
): Promise<ClientMerkleTree> {
  const sq = getSqlocal();
  const allRows =
    (await sq.sql`SELECT entity_id, hash FROM entity_hashes ORDER BY entity_id`) as {
      entity_id: string;
      hash: string;
    }[];
  const rows = allowedEntityIds
    ? allRows.filter((r) => allowedEntityIds.has(r.entity_id))
    : allRows;

  // Level 2: group entities into 4-char prefix buckets (hash-based)
  const bucketMap = new Map<string, { entityId: string; hash: string }[]>();
  for (const row of rows) {
    const prefix = getBucketPrefix(row.entity_id);
    const list = bucketMap.get(prefix) || [];
    list.push({ entityId: row.entity_id, hash: row.hash });
    bucketMap.set(prefix, list);
  }

  const buckets: MerkleBucket[] = [];
  for (const [prefix, entities] of bucketMap) {
    entities.sort((a, b) => a.entityId.localeCompare(b.entityId));
    const combined = entities.map((e) => `${e.entityId}:${e.hash}`).join("|");
    buckets.push({
      prefix,
      hash: fnv1a(combined),
      entityCount: entities.length,
    });
  }
  buckets.sort((a, b) => a.prefix.localeCompare(b.prefix));

  // Level 1: group leaf buckets into 2-char prefix groups
  const groupMap = new Map<string, MerkleBucket[]>();
  for (const bucket of buckets) {
    const gp = bucket.prefix.slice(0, 2);
    const list = groupMap.get(gp) || [];
    list.push(bucket);
    groupMap.set(gp, list);
  }

  const groups: MerkleGroup[] = [];
  for (const [prefix, children] of groupMap) {
    const groupHash = fnv1a(
      children.map((b) => `${b.prefix}:${b.hash}`).join("|"),
    );
    groups.push({ prefix, hash: groupHash });
  }
  groups.sort((a, b) => a.prefix.localeCompare(b.prefix));

  const root = fnv1a(groups.map((g) => `${g.prefix}:${g.hash}`).join("|"));

  return { root, groups, buckets };
}

/** Look up cached entity hashes from the denormalized table. */
async function getClientEntityHashes(
  entityIds: string[],
): Promise<Map<string, string>> {
  const result = new Map<string, string>();
  if (entityIds.length === 0) return result;

  const sq = getSqlocal();
  const rows = (await sq.sql`SELECT entity_id, hash FROM entity_hashes`) as {
    entity_id: string;
    hash: string;
  }[];
  for (const row of rows) {
    result.set(row.entity_id, row.hash);
  }
  for (const eid of entityIds) {
    if (!result.has(eid)) result.set(eid, "");
  }
  return result;
}

type SyncListener = (event: EventEnvelope) => void;

interface PendingQuery {
  resolve: (result: unknown) => void;
  reject: (error: Error) => void;
}

export class SyncEngine {
  private ws: WebSocket | null = null;
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private pruneTimer: ReturnType<typeof setInterval> | null = null;
  private pingTimer: ReturnType<typeof setInterval> | null = null;
  private blobListenerCleanup: (() => void) | null = null;
  private reconnectDelay = 1000;
  private listeners = new Set<SyncListener>();
  private invalidateListeners = new Set<(resources: string[]) => void>();
  private pendingQueries = new Map<string, PendingQuery>();
  private commandWaiters = new Map<
    string,
    { resolve: () => void; reject: (err: Error) => void }
  >();
  private _online = false;
  private _connecting = false;
  private _syncing = true; // True until initial sync completes
  private _pendingCount = 0;
  private _pendingBlobCount = 0;
  private _connectTime = 0;
  // Sequential message processing queue to avoid concurrent SQLite writes
  private messageQueue: Promise<void> = Promise.resolve();
  // Buffer event batches during resync; process all at once when 'synced' arrives
  private resyncBuffer: EventEnvelope[] = [];
  private stopped = false;
  // Selective sync: case IDs currently subscribed on the server
  private subscribedCaseIds = new Set<string>();
  // Guard against duplicate concurrent syncCase calls (e.g. StrictMode double-fire)
  private syncingCaseIds = new Map<string, Promise<void>>();
  // Cached local Merkle tree for drilldown comparison
  private localTree: ClientMerkleTree | null = null;
  // Auth failure detection: count consecutive connection failures where
  // the server never sent a hello (indicates 401 rejection on upgrade).
  private _gotHello = false;
  private _consecutiveAuthFailures = 0;
  private static readonly AUTH_FAILURE_THRESHOLD = 2;
  // Microtask batching for commands
  private pendingBatch: CommandEnvelope[] = [];
  private batchScheduled = false;

  constructor(
    private wsUrl: string,
    private sessionId: string,
    private applyProjection: (event: EventEnvelope) => Promise<void>,
    private onAuthFailure?: () => void,
    private onVersionMismatch?: () => void,
  ) {}

  get online() {
    return this._online;
  }
  get connecting() {
    return this._connecting;
  }
  get syncing() {
    return this._syncing;
  }
  get pendingCount() {
    return this._pendingCount;
  }
  get pendingBlobCount() {
    return this._pendingBlobCount;
  }

  onEvent(listener: SyncListener): () => void {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  }

  onInvalidate(listener: (resources: string[]) => void): () => void {
    this.invalidateListeners.add(listener);
    return () => {
      this.invalidateListeners.delete(listener);
    };
  }

  private notifyInvalidate(resources: string[]) {
    for (const listener of this.invalidateListeners) {
      try {
        listener(resources);
      } catch {}
    }
  }

  private handleBrowserOffline = () => {
    console.log("[Sync] Browser went offline");
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
    this.ws?.close();
  };

  private handleBrowserOnline = () => {
    console.log("[Sync] Browser came back online");
    if (this.stopped) return;
    // Reconnect immediately instead of waiting for backoff timer
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
    this.reconnectDelay = 1000;
    this.connect();
  };

  async start() {
    await this.updatePendingCount();
    await this.updatePendingBlobCount();
    if (this.stopped) return;
    this.startPruneTimer();
    this.registerBlobListener();
    window.addEventListener("offline", this.handleBrowserOffline);
    window.addEventListener("online", this.handleBrowserOnline);
    this.connect();
  }

  stop() {
    this.stopped = true;
    if (this.reconnectTimer) clearTimeout(this.reconnectTimer);
    this.stopPing();
    this.stopPruneTimer();
    this.blobListenerCleanup?.();
    this.blobListenerCleanup = null;
    window.removeEventListener("offline", this.handleBrowserOffline);
    window.removeEventListener("online", this.handleBrowserOnline);
    this.ws?.close();
    this.ws = null;
    this._online = false;
    this._connecting = false;
  }

  private connect() {
    if (this.stopped) return;
    this._connecting = true;
    this.notify(null!);
    this._gotHello = false;
    const url = `${this.wsUrl}?session=${this.sessionId}`;
    console.log("[Sync] Connecting to", url);
    this.ws = new WebSocket(url);
    this.ws.binaryType = "arraybuffer";

    this.ws.onopen = () => {
      this._connectTime = performance.now();
      console.log("[Sync] Connected, waiting for server hello...");
      this.reconnectDelay = 1000;
      // Don't set online yet - wait for hello handshake
    };

    this.ws.onmessage = (event) => {
      const data =
        event.data instanceof ArrayBuffer
          ? new Uint8Array(event.data)
          : event.data;
      const msg = decode<ServerMessage>(data);
      // Queue message processing to ensure sequential execution
      this.messageQueue = this.messageQueue
        .then(() => this.processMessage(msg))
        .catch((e) => {
          console.error("Message handling error:", e);
          if (e instanceof Error && /sqlite/i.test(e.message)) reportDbError(e);
        });
    };

    this.ws.onclose = async (event) => {
      console.log(
        "[Sync] Disconnected:",
        event.code,
        event.reason || "(no reason)",
      );
      this._online = false;
      this.stopPing();
      this.resyncBuffer = [];

      // Reject all pending queries
      for (const [, pending] of this.pendingQueries) {
        pending.reject(new Error("Connection closed"));
      }
      this.pendingQueries.clear();
      this.notify(null!);

      // Detect server 401 rejection: upgrade failed before we got a hello.
      // Code 1006 = abnormal closure (upgrade rejected), no hello = auth failure.
      // But 1006 also fires on network errors, so verify with /api/me before logging out.
      if (!this._gotHello && event.code === 1006) {
        this._consecutiveAuthFailures++;
        console.warn(
          `[Sync] Auth failure ${this._consecutiveAuthFailures}/${SyncEngine.AUTH_FAILURE_THRESHOLD}`,
        );
        if (
          this._consecutiveAuthFailures >= SyncEngine.AUTH_FAILURE_THRESHOLD
        ) {
          // Confirm the session is actually rejected, not just a network issue
          const apiBase = this.wsUrl
            .replace(/^ws/, "http")
            .replace(/\/ws$/, "");
          try {
            const res = await fetch(
              `${apiBase}/api/me?session=${this.sessionId}`,
            );
            if (res.status >= 400 && res.status < 500) {
              console.error(
                "[Sync] Session confirmed rejected by server, logging out",
              );
              this.stopped = true;
              this.onAuthFailure?.();
              return;
            }
          } catch {
            /* network error — don't logout */
          }
          // Server is reachable and session is valid, or network is down — reset counter
          this._consecutiveAuthFailures = 0;
        }
      }

      this.scheduleReconnect();
    };

    this.ws.onerror = (error) => {
      console.error("[Sync] WebSocket error:", error);
      this.ws?.close();
    };
  }

  private async processMessage(msg: ServerMessage) {
    switch (msg.tag) {
      case "hello": {
        console.log(
          `[Sync] Server hello (${(performance.now() - this._connectTime).toFixed(0)}ms after connect), version:`,
          msg.version,
        );
        this._gotHello = true;
        this._consecutiveAuthFailures = 0;

        // Check protocol version compatibility
        if (msg.version !== PROTOCOL_VERSION) {
          console.error(
            `[Sync] Protocol version mismatch: client=${PROTOCOL_VERSION}, server=${msg.version}`,
          );
          this.ws?.close();
          this.stopped = true;
          this.onVersionMismatch?.();
          return;
        }

        // Check app version compatibility
        if (msg.minAppVersion && APP_VERSION < msg.minAppVersion) {
          console.error(
            `[Sync] App version too old: client=${APP_VERSION}, server requires>=${msg.minAppVersion}`,
          );
          this.ws?.close();
          this.stopped = true;
          this.onVersionMismatch?.();
          return;
        }

        const hello: ClientMessage = {
          tag: "hello",
          version: PROTOCOL_VERSION,
          appVersion: APP_VERSION,
        };
        this.ws!.send(encode(hello));
        this._online = true;
        this._connecting = false;
        this.startPing();
        this.notify(null!);
        // Merge server-provided recent case IDs into local retention
        if (msg.recentCaseIds && msg.recentCaseIds.length > 0) {
          for (const id of msg.recentCaseIds) {
            await markCaseAccessed(id);
          }
        }
        // Subscribe BEFORE merkle so the server scopes the Merkle tree to our cases
        const casesToKeep = await getCasesToKeep();
        this.subscribedCaseIds = casesToKeep;
        const caseIds = [...casesToKeep];
        console.log(`[Sync] Subscribing to ${caseIds.length} cases`);
        const subMsg: ClientMessage = { tag: "subscribe", caseIds };
        this.ws!.send(encode(subMsg));
        // Flush pending commands BEFORE merkle so server has all our events
        // (WS is ordered — server processes commands before the merkle message)
        await this.flushPending();
        // Now send Merkle tree scoped to the same entity set as the server
        const t0 = performance.now();
        const localScope = await resolveLocalScope(caseIds);
        this.localTree = await computeClientMerkleTree(localScope);
        console.log(
          `[Sync] Sending Merkle tree: ${this.localTree.groups.length} groups, ${this.localTree.buckets.length} buckets, ${localScope.size} entities in scope (${(performance.now() - t0).toFixed(0)}ms)`,
        );
        const merkleMsg: ClientMessage = {
          tag: "merkle",
          root: this.localTree.root,
          groups: this.localTree.groups,
        };
        this.ws!.send(encode(merkleMsg));
        await this.uploadPendingBlobs();
        break;
      }
      case "event":
        await this.handleEvent(msg.envelope);
        dispatchWatchEventIfNeeded(msg.envelope);
        if (msg.envelope.type === "fieldEvent.stop") {
          window.dispatchEvent(new CustomEvent("biostream:field-event-stopped", {
            detail: { fieldEventId: msg.envelope.entityId },
          }));
        }
        if (msg.envelope.type === "fieldEvent.assignTriage") {
          const p = msg.envelope.payload as any;
          window.dispatchEvent(new CustomEvent("biostream:triage-assigned", {
            detail: { fieldEventId: p.uuid, watchId: p.watchId, medicId: p.medicUuid },
          }));
        }
        if (msg.envelope.type === "fieldEvent.triageComplete") {
          const p = msg.envelope.payload as any;
          window.dispatchEvent(new CustomEvent("biostream:triage-complete", {
            detail: { fieldEventId: p.uuid, watchId: p.watchId },
          }));
        }
        this.notify(null!);
        break;
      case "events": {
        // Buffer batches during resync; they'll be processed when 'synced' arrives
        console.log(
          `[Sync] Buffered ${msg.envelopes.length} events (total buffered: ${this.resyncBuffer.length + msg.envelopes.length})`,
        );
        this.resyncBuffer.push(...msg.envelopes);
        break;
      }
      case "drilldown": {
        // Server sent leaf buckets for divergent groups — compare with local buckets
        const serverBuckets = new Map(
          msg.buckets.map((b) => [b.prefix, b.hash]),
        );
        const clientBuckets = new Map(
          (this.localTree?.buckets || []).map((b) => [b.prefix, b.hash]),
        );

        const divergentPrefixes: string[] = [];
        // Buckets on server that differ or are missing on client
        for (const [prefix, h] of serverBuckets) {
          if (clientBuckets.get(prefix) !== h) {
            divergentPrefixes.push(prefix);
          }
        }

        console.log(
          `[Sync] Drilldown: ${divergentPrefixes.length} divergent buckets out of ${serverBuckets.size} server buckets`,
        );

        if (divergentPrefixes.length === 0) {
          // No divergent buckets — sync complete
          console.log(
            "[Sync] No divergent buckets after drilldown, sync complete",
          );
          this._syncing = false;
          this.notify(null!);
          this.notifyInvalidate(["synced"]);
          break;
        }

        const drillMsg: ClientMessage = {
          tag: "drilldown",
          prefixes: divergentPrefixes,
        };
        this.ws!.send(encode(drillMsg));
        break;
      }
      case "divergent": {
        console.log(
          `[Sync] Received ${msg.entities.length} divergent entity hashes`,
        );

        // Fast path: if client has no events at all, everything is divergent
        const countRows = await db
          .select({ count: sql<number>`count(*)` })
          .from(events);
        const totalEvents = countRows[0]?.count ?? 0;
        let divergentEntities: string[];

        if (totalEvents === 0) {
          divergentEntities = msg.entities.map((e) => e.entityId);
        } else {
          const clientHashes = await getClientEntityHashes(
            msg.entities.map((e) => e.entityId),
          );
          divergentEntities = [];
          for (const { entityId, hash: serverHash } of msg.entities) {
            const clientHash = clientHashes.get(entityId);
            if (clientHash !== serverHash) {
              divergentEntities.push(entityId);
            }
          }
        }

        console.log(
          `[Sync] Requesting resync for ${divergentEntities.length} entities`,
        );
        const resyncMsg: ClientMessage = {
          tag: "resync",
          entityIds: divergentEntities,
        };
        this.ws!.send(encode(resyncMsg));
        break;
      }
      case "synced": {
        // Process all buffered resync events as a single batch
        if (this.resyncBuffer.length > 0) {
          const t0 = performance.now();
          const buffered = this.resyncBuffer;
          this.resyncBuffer = [];
          console.log(`[Sync] Processing ${buffered.length} buffered events`);
          await this.handleEventBatch(buffered);
          console.log(
            `[Sync] Batch done in ${(performance.now() - t0).toFixed(0)}ms`,
          );
        }
        console.log("[Sync] Initial sync complete");
        this._syncing = false;
        this.notify(null!);
        // Signal that server-side queries are now available
        this.notifyInvalidate(["synced"]);
        break;
      }
      case "batchAck":
        // All commands in batch succeeded — delete in one query
        await db.delete(pendingCommands).where(inArray(pendingCommands.id, msg.commandIds));
        for (const cmdId of msg.commandIds) {
          this.commandWaiters.get(cmdId)?.resolve();
          this.commandWaiters.delete(cmdId);
        }
        await this.updatePendingCount();
        this.notify(null!);
        break;
      case "batchError":
        // Batch failed — revert ALL optimistic events in the batch
        console.error(
          `Batch failed at index ${msg.failedIndex}: [${msg.code}] ${msg.message}`,
        );
        for (const cmdId of msg.commandIds) {
          await this.revertOptimisticEvent(cmdId);
          await db.delete(pendingCommands).where(eq(pendingCommands.id, cmdId));
          this.commandWaiters.get(cmdId)?.reject(new Error(msg.message));
          this.commandWaiters.delete(cmdId);
        }
        await this.updatePendingCount();
        this.notify(null!);
        break;
      case "response": {
        const pending = this.pendingQueries.get(msg.id);
        if (pending) {
          this.pendingQueries.delete(msg.id);
          pending.resolve(msg.result);
        }
        break;
      }
      case "queryError": {
        const pending = this.pendingQueries.get(msg.id);
        if (pending) {
          this.pendingQueries.delete(msg.id);
          pending.reject(new Error(`[${msg.code}] ${msg.message}`));
        }
        break;
      }
      case "invalidate":
        this.notifyInvalidate(msg.resources);
        break;
      case "pong":
        break;
      case "fieldEventInvite":
        window.dispatchEvent(new CustomEvent("biostream:field-event-invite", {
          detail: {
            fieldEventId: msg.fieldEventId,
            invitedByUserId: msg.invitedByUserId,
            invitedByName: msg.invitedByName,
          },
        }));
        break;
    }
  }

  private scheduleReconnect() {
    if (this.stopped) return;
    if (this.reconnectTimer) clearTimeout(this.reconnectTimer);
    console.log(`[Sync] Reconnecting in ${this.reconnectDelay}ms...`);
    this.reconnectTimer = setTimeout(() => {
      this.reconnectDelay = Math.min(this.reconnectDelay * 2, 30000);
      this.connect();
    }, this.reconnectDelay);
  }

  private registerBlobListener() {
    this.blobListenerCleanup = onBlobStored((sha256) => {
      if (this._online) {
        this.uploadBlobImmediately(sha256);
      }
    });
  }

  private async uploadBlobImmediately(sha256: string) {
    try {
      const success = await uploadBlob(sha256);
      if (success) {
        console.log(`Uploaded blob ${sha256.slice(0, 8)}...`);
      }
      await this.updatePendingBlobCount();
    } catch (e) {
      console.warn("Blob upload error:", e);
    }
  }

  private async uploadPendingBlobs() {
    try {
      const uploaded = await uploadAllPending();
      if (uploaded > 0) {
        console.log(`Uploaded ${uploaded} pending blobs`);
      }
      await this.updatePendingBlobCount();
    } catch (e) {
      console.warn("Blob upload error:", e);
    }
  }

  private async updatePendingBlobCount() {
    this._pendingBlobCount = await getPendingBlobCount();
  }

  private startPing() {
    this.stopPing();
    this.pingTimer = setInterval(() => {
      if (this.ws?.readyState === WebSocket.OPEN) {
        this.ws.send(encode({ tag: "ping" } as ClientMessage));
      }
    }, 15_000);
  }

  private stopPing() {
    if (this.pingTimer) {
      clearInterval(this.pingTimer);
      this.pingTimer = null;
    }
  }

  private startPruneTimer() {
    this.stopPruneTimer();
    // Prune every 5 minutes
    this.pruneTimer = setInterval(
      () => {
        this.pruneOldCases();
      },
      5 * 60 * 1000,
    );
  }

  private stopPruneTimer() {
    if (this.pruneTimer) {
      clearInterval(this.pruneTimer);
      this.pruneTimer = null;
    }
  }

  private async pruneOldCases() {
    try {
      const pruned = await pruneCases();
      if (pruned > 0) {
        console.log(`Pruned ${pruned} old cases`);
      }
    } catch (e) {
      console.warn("Case pruning error:", e);
    }
  }

  /**
   * Pin a case as locally created (will be retained until synced).
   */
  async pinLocalCase(caseId: string): Promise<void> {
    await pinCase(caseId);
  }

  /**
   * Unpin a case (e.g., after confirmed synced to server).
   */
  async unpinLocalCase(caseId: string): Promise<void> {
    await unpinCase(caseId);
  }

  /**
   * Send a subscribe message with the current set of retained case IDs.
   * Called after initial sync and whenever the subscription set changes.
   */
  private async sendSubscription(): Promise<void> {
    if (!this._online || !this.ws || this.ws.readyState !== WebSocket.OPEN)
      return;
    const casesToKeep = await getCasesToKeep();
    this.subscribedCaseIds = casesToKeep;
    const caseIds = [...casesToKeep];
    const msg: ClientMessage = { tag: "subscribe", caseIds };
    console.log(`[Sync] Subscribing to ${caseIds.length} cases`);
    this.ws.send(encode(msg));
  }

  /**
   * Fetch all events for a case from the server, insert locally, and rebuild projections.
   * Used when opening a case that isn't yet in the local database.
   */
  async syncCase(caseId: string): Promise<void> {
    // Deduplicate concurrent calls for the same case (e.g. StrictMode double-fire)
    const existing = this.syncingCaseIds.get(caseId);
    if (existing) return existing;

    const p = (async () => {
      console.log(`[Sync] syncCase ${caseId.slice(0, 8)}`);
      const result = await this.query<{ events: EventEnvelope[] }>({
        type: "syncCase",
        caseId,
      });
      if (result.events.length > 0) {
        await this.handleEventBatch(result.events);
      }
      // Mark as accessed and re-subscribe with updated case set
      await markCaseAccessed(caseId);
      await this.sendSubscription();
      // Notify listeners so useQuery hooks re-run and pick up the new data
      this.notify(null!);
    })();

    this.syncingCaseIds.set(caseId, p);
    try {
      await p;
    } finally {
      this.syncingCaseIds.delete(caseId);
    }
  }

  /**
   * Fetch all events for a single entity from the server, insert locally, and rebuild.
   * Used to pull a patient's data when linking it to a case.
   */
  async syncEntity(entityId: string): Promise<void> {
    const result = await this.query<{ events: EventEnvelope[] }>({
      type: "syncEntity",
      entityId,
    });
    if (result.events.length > 0) {
      await this.handleEventBatch(result.events);
    }
    this.notify(null!);
  }

  /**
   * Open a case: ensure it's synced locally, mark as accessed, update subscription.
   * Call this when the user navigates to a case detail page.
   */
  async openCase(caseId: string): Promise<void> {
    // If already subscribed, just mark access
    if (this.subscribedCaseIds.has(caseId)) {
      await markCaseAccessed(caseId);
      return;
    }
    // Not in subscription — fetch from server first
    if (this._online) {
      await this.syncCase(caseId);
    } else {
      // Offline — just mark access, will sync when online
      await markCaseAccessed(caseId);
    }
  }

  private async handleEvent(event: EventEnvelope) {
    // Insert into local events table, or update serverTimestamp if optimistic event exists
    try {
      await db
        .insert(events)
        .values({
          id: event.id,
          type: event.type,
          entityId: event.entityId,
          payload: event.payload as unknown as object,
          userId: event.userId,
          sessionId: event.sessionId,
          clientTimestamp: event.clientTimestamp,
          serverTimestamp: event.serverTimestamp,
        })
        .onConflictDoUpdate({
          target: events.id,
          set: { serverTimestamp: event.serverTimestamp },
        });

      await this.applyProjection(event);
      await updateEntityHash(event.entityId);

      // Track case access for retention (case.* and sub-entity events with caseUuid)
      const prefix = event.type.split(".")[0];
      if (
        prefix === "case" ||
        prefix === "vitals" ||
        prefix === "ventilation" ||
        prefix === "diagnostics" ||
        prefix === "labValues" ||
        prefix === "exam" ||
        prefix === "cpr" ||
        prefix === "tourniquet" ||
        prefix === "medication" ||
        prefix === "surgery" ||
        prefix === "attachment"
      ) {
        const caseId = this.extractCaseId(event);
        if (caseId) {
          await markCaseAccessed(caseId);
        }
      }
    } catch (e) {
      // Duplicate or error — skip
      console.warn("Event apply error:", e);
      if (e instanceof Error && /sqlite/i.test(e.message)) reportDbError(e);
    }

    this.notify(event);
  }

  /**
   * Optimized batch processing: insert all events first, then rebuild unique entities.
   * Much faster than processing events one-by-one with projections.
   */
  private async handleEventBatch(batch: EventEnvelope[]) {
    const t0 = performance.now();

    // Step 1: Insert all events using SQLocal batch (single worker round-trip)
    const sq = getSqlocal();
    await sq.batch((sql) =>
      batch.map((event) => {
        const payload = JSON.stringify(event.payload);
        return sql`INSERT INTO events (id, type, entity_id, payload, user_id, session_id, client_timestamp, server_timestamp)
        VALUES (${event.id}, ${event.type}, ${event.entityId}, ${payload}, ${event.userId}, ${event.sessionId}, ${event.clientTimestamp}, ${event.serverTimestamp})
        ON CONFLICT(id) DO UPDATE SET server_timestamp = ${event.serverTimestamp}`;
      }),
    );
    console.log(
      `[Sync]   insert ${batch.length} events: ${(performance.now() - t0).toFixed(0)}ms`,
    );

    // Step 2: Collect unique entities (prefix:entityId)
    const entities = new Map<string, EventEnvelope>();
    for (const event of batch) {
      const prefix = event.type.split(".")[0];
      const key = `${prefix}:${event.entityId}`;
      entities.set(key, event); // Keep last event for each entity
    }

    // Step 3: Sort by dependency order — foundational entities first
    // Cases depend on patients (for merge chain + unit denormalization),
    // timeseries entities depend on cases, etc.
    const REBUILD_ORDER: Record<string, number> = {
      region: 0,
      hospital: 1,
      unit: 2,
      user: 3,
      session: 4,
      patient: 5,
      case: 6,
      vitals: 7,
      ventilation: 7,
      diagnostics: 7,
      labValues: 7,
      exam: 7,
      cpr: 7,
      tourniquet: 7,
      medication: 7,
      surgery: 7,
      attachment: 7,
    };
    const sorted = [...entities.entries()].sort((a, b) => {
      const prefixA = a[0].split(":")[0];
      const prefixB = b[0].split(":")[0];
      return (REBUILD_ORDER[prefixA] ?? 99) - (REBUILD_ORDER[prefixB] ?? 99);
    });

    // Step 4: Rebuild projections for unique entities only
    const t1 = performance.now();
    console.log(`[Sync]   rebuilding ${sorted.length} entities`);
    for (const [, event] of sorted) {
      try {
        await this.applyProjection(event);
      } catch (e) {
        console.warn("Projection error:", e);
        if (e instanceof Error && /sqlite/i.test(e.message)) reportDbError(e);
      }
    }
    console.log(
      `[Sync]   projections: ${(performance.now() - t1).toFixed(0)}ms`,
    );

    // Step 5: Update denormalized entity hashes
    const t2 = performance.now();
    const uniqueEntityIds = [...new Set(batch.map((e) => e.entityId))];
    await updateEntityHashes(uniqueEntityIds);
    console.log(
      `[Sync]   hashes (${uniqueEntityIds.length}): ${(performance.now() - t2).toFixed(0)}ms`,
    );
  }

  private extractCaseId(event: EventEnvelope): string | null {
    const prefix = event.type.split(".")[0];
    // For case events, entityId is the case ID
    if (prefix === "case") {
      return event.entityId;
    }
    // For sub-entity create events, caseUuid is in the payload
    const payload = event.payload as { caseUuid?: string; caseId?: string };
    if (payload.caseUuid && typeof payload.caseUuid === "string") {
      return payload.caseUuid;
    }
    if (payload.caseId && typeof payload.caseId === "string") {
      return payload.caseId;
    }
    return null;
  }

  /**
   * Revert an optimistic event that was rejected by the server.
   * Deletes the event and rebuilds the projection from remaining events.
   */
  private async revertOptimisticEvent(eventId: string): Promise<void> {
    // Find the event to get its entityId and type for rebuilding
    const [event] = await db
      .select()
      .from(events)
      .where(eq(events.id, eventId));
    if (!event) {
      console.log(
        `[Sync] Event ${eventId.slice(0, 8)} not found, nothing to revert`,
      );
      return;
    }

    console.log(
      `[Sync] Reverting optimistic event ${eventId.slice(0, 8)} for entity ${event.entityId.slice(0, 8)}`,
    );

    // Delete the rejected event
    await db.delete(events).where(eq(events.id, eventId));

    // Rebuild the projection from remaining events
    // We just need entityId and type to trigger the right rebuilder
    await this.applyProjection({
      id: eventId,
      type: event.type,
      entityId: event.entityId,
      payload: event.payload,
      userId: event.userId,
      sessionId: event.sessionId,
      clientTimestamp: event.clientTimestamp,
      serverTimestamp: event.serverTimestamp,
    } as EventEnvelope);

    // Update denormalized hash after deletion
    await updateEntityHash(event.entityId);
  }

  /**
   * Send many commands in a single WebSocket message without per-command
   * optimistic writes. Intended for bulk import where you don't need local
   * optimistic updates. Waits for one batchAck covering all commands.
   */
  async bulkDispatch(cmds: CommandEnvelope[]): Promise<void> {
    if (cmds.length === 0) return;

    // Register all waiters synchronously before any await
    const ackPromises = cmds.map(
      (cmd) =>
        new Promise<void>((resolve, reject) => {
          this.commandWaiters.set(cmd.id, { resolve, reject });
        }),
    );

    // Send as a single batch message directly (bypass pendingBatch/microtask)
    if (this._online && this.ws?.readyState === WebSocket.OPEN) {
      const msg: ClientMessage = { tag: "commands", envelopes: cmds };
      this.ws.send(encode(msg));
    }

    // Persist all in one multi-row insert
    const now = Date.now();
    await db.insert(pendingCommands).values(
      cmds.map((cmd) => ({
        id: cmd.id,
        sessionId: this.sessionId,
        envelope: cmd as unknown as Record<string, unknown>,
        createdAt: now,
      })),
    );
    await this.updatePendingCount();

    await Promise.all(ackPromises);
  }

  async dispatch(cmd: CommandEnvelope): Promise<void> {
    // Register waiter synchronously before any await, so batchAck can't arrive
    // before the waiter is in place.
    const ackPromise = new Promise<void>((resolve, reject) => {
      this.commandWaiters.set(cmd.id, { resolve, reject });
    });

    // Push to batch synchronously before any await — if multiple dispatches are
    // in-flight concurrently they all queue here before the microtask fires,
    // so they go out in one WebSocket message rather than one each.
    if (this._online && this.ws?.readyState === WebSocket.OPEN) {
      this.pendingBatch.push(cmd);
      if (!this.batchScheduled) {
        this.batchScheduled = true;
        queueMicrotask(() => this.flushBatch());
      }
    }

    // Persist after scheduling the send — command survives reconnects via
    // flushPending(), and any divergence is caught by Merkle resync.
    await db.insert(pendingCommands).values({
      id: cmd.id,
      sessionId: this.sessionId,
      envelope: cmd as unknown as Record<string, unknown>,
      createdAt: Date.now(),
    });
    await this.updatePendingCount();

    return ackPromise;
  }

  private flushBatch(): void {
    this.batchScheduled = false;
    if (
      this.pendingBatch.length === 0 ||
      !this.ws ||
      this.ws.readyState !== WebSocket.OPEN
    )
      return;

    const batch = this.pendingBatch;
    this.pendingBatch = [];
    const msg: ClientMessage = { tag: "commands", envelopes: batch };
    this.ws.send(encode(msg));
  }

  /**
   * Send a query and wait for the response.
   * Throws if not connected or if the query fails.
   */
  query<T = unknown>(queryType: QueryType): Promise<T> {
    return new Promise((resolve, reject) => {
      if (!this._online || !this.ws || this.ws.readyState !== WebSocket.OPEN) {
        reject(new Error("Not connected"));
        return;
      }

      const id = uuidv7();
      this.pendingQueries.set(id, {
        resolve: resolve as (result: unknown) => void,
        reject,
      });

      const msg: ClientMessage = { tag: "query", id, query: queryType };
      this.ws.send(encode(msg));

      // Timeout after 30 seconds
      setTimeout(() => {
        if (this.pendingQueries.has(id)) {
          this.pendingQueries.delete(id);
          reject(new Error("Query timeout"));
        }
      }, 30000);
    });
  }

  private async flushPending() {
    // Only flush commands for the current session, send as single batch
    const pending = await db
      .select()
      .from(pendingCommands)
      .where(eq(pendingCommands.sessionId, this.sessionId))
      .orderBy(pendingCommands.createdAt);
    if (pending.length === 0 || !this.ws) return;
    const envelopes = pending.map(
      (row) => row.envelope as unknown as CommandEnvelope,
    );
    const msg: ClientMessage = { tag: "commands", envelopes };
    this.ws.send(encode(msg));
  }

  private async updatePendingCount() {
    // Only count commands for the current session
    const rows = await db
      .select({ count: sql<number>`count(*)` })
      .from(pendingCommands)
      .where(eq(pendingCommands.sessionId, this.sessionId));
    this._pendingCount = rows[0]?.count ?? 0;
  }

  private notify(event: EventEnvelope) {
    for (const listener of this.listeners) {
      try {
        listener(event);
      } catch {}
    }
  }
}
